import Decimal from 'decimal.js';
import { escapeCsvField } from './csvExport';
import { fiBasisFilingAuditNote } from './fiDisposalBasis';
import { nativeCroEurPerUnit } from './cryptoPrices';
import { croUsdPerUnitOnUtcDayNearest } from './croUsdDaily';
import { usdToEurOnDateWithResolvedDay } from './eurFx';
import { TAX_PIPELINE } from './taxPipelineConfig';
import type { DisposalDetail } from './types';

/**
 * Excel formula list separator. Finnish Excel uses `;` (US/UK often use `,`).
 * If formulas show as text after opening the CSV, switch Excel locale or replace `;` with `,` in formulas.
 */
const XL = ';';

/** 1-based column index → Excel column letter(s) (1→A, 27→AA). */
export function excelColumnLetterFromIndex(oneBased: number): string {
  let n = oneBased;
  let s = '';
  while (n > 0) {
    const m = (n - 1) % 26;
    s = String.fromCharCode(65 + m) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

function utcDateKeyFromTs(ts: number): string {
  return new Date(ts).toISOString().slice(0, 10);
}

/** CSV headings: meta + per-disposal FIFO / deemed / chosen + ECB USD/EUR + manual USD helper columns. */
export const FINNISH_CAPITAL_GAINS_HEADERS = [
  'policy_mode',
  'basis_method',
  'timezone',
  'filing_basis_audit_note',
  'Asset Amount',
  'Asset Name',
  'Date of Sale',
  'Date of Acquisition',
  'Proceeds (EUR)',
  'proceeds_derivation',
  'valuation_flag',
  'fifo_cost_basis_eur',
  'deemed_cost_basis_eur',
  'chosen_cost_basis_eur',
  'basis_method_used',
  'deemed_prevented_zero_proceeds',
  'gain_loss_eur_fifo',
  'gain_loss_eur_deemed',
  'gain_loss_eur_chosen_filing',
  'deemed_holding_bracket',
  'lot_basis_repaired',
  'lot_basis_repair_method',
  'lot_basis_repair_confidence',
  'lot_basis_repair_source_ref',
  /** ECB (Frankfurter): 1 USD = this many EUR, for the disposal (sale) UTC day. */
  'ecb_usd_to_eur_rate_sale_date',
  /** Calendar date (YYYY-MM-DD) of the ECB rate above (may differ on weekends/holidays). */
  'ecb_rate_reference_date_utc',
  /** Bundled daily CRO/USD (`cro-usd-max.csv`): USD per 1 CRO on the sale UTC day (nearest day if gap). */
  'cro_usd_per_unit_sale_date',
  /** Same CRO/USD × ECB USD→EUR — matches `nativeCroEurPerUnit` / Cronos pipeline. */
  'cro_eur_per_unit_sale_date',
  /** Fill manually: gross proceeds in USD (e.g. from explorer). */
  'manual_proceeds_usd',
  /** = manual_usd * ecb_rate (Excel); use when Proceeds (EUR) is missing. */
  'proceeds_eur_from_manual_usd',
  /** = proceeds_eur_from_manual − fifo_cost_basis (Excel); sanity-check vs gain_loss_eur_fifo. */
  'gain_loss_eur_fifo_if_manual_proceeds',
] as const;

/** DD/MM/YYYY HH:MM:SS in UTC (same instant as on-chain timestamps). */
export function formatUtcDateTimeFi(ts: number): string {
  const d = new Date(ts);
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const yyyy = d.getUTCFullYear();
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mi = String(d.getUTCMinutes()).padStart(2, '0');
  const ss = String(d.getUTCSeconds()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}:${ss}`;
}

/**
 * Thousands separator (space) + 8 decimals, e.g. `9 999.99633549`.
 */
export function formatAssetAmountDisplay(d: Decimal): string {
  const s = d.toFixed(8);
  const parts = s.split('.');
  const intPart = parts[0] ?? '0';
  const frac = parts[1];
  const withSpaces = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return frac !== undefined ? `${withSpaces}.${frac}` : withSpaces;
}

function headerColLetter(name: string): string {
  const idx = FINNISH_CAPITAL_GAINS_HEADERS.indexOf(
    name as (typeof FINNISH_CAPITAL_GAINS_HEADERS)[number]
  );
  if (idx < 0) throw new Error(`Unknown Finnish capital gains column: ${name}`);
  return excelColumnLetterFromIndex(idx + 1);
}

/**
 * One row per FIFO disposal line with Finnish deemed + chosen basis (use `applyFiBasisToDisposalDetails` first).
 * Fetches ECB USD→EUR (Frankfurter) per unique disposal day; bundled CRO/USD + CRO/EUR for that day;
 * includes Excel formulas for optional manual USD proceeds.
 */
export async function buildFinnishCapitalGainsCsv(details: DisposalDetail[]): Promise<string> {
  const meta = TAX_PIPELINE.fi;
  const auditNote = fiBasisFilingAuditNote();

  const uniqueDays = Array.from(new Set(details.map((d) => utcDateKeyFromTs(d.ts))));
  const rateByDay = new Map<string, { rate: Decimal; rateDateIso: string }>();
  const croByDay = new Map<string, { usd: string; eur: string }>();
  await Promise.all(
    uniqueDays.map(async (day) => {
      const noonUtc = new Date(`${day}T12:00:00.000Z`);
      const { rate, rateDateIso } = await usdToEurOnDateWithResolvedDay(noonUtc);
      rateByDay.set(day, { rate, rateDateIso });

      const usd = croUsdPerUnitOnUtcDayNearest(noonUtc);
      const eurUnit = await nativeCroEurPerUnit(noonUtc);
      croByDay.set(day, {
        usd: usd != null && Number.isFinite(usd) ? String(usd) : '',
        eur: eurUnit != null && eurUnit.gt(0) ? eurUnit.toFixed(8) : '',
      });
    })
  );

  const colRate = headerColLetter('ecb_usd_to_eur_rate_sale_date');
  const colManual = headerColLetter('manual_proceeds_usd');
  const colProceedsManual = headerColLetter('proceeds_eur_from_manual_usd');
  const colFifo = headerColLetter('fifo_cost_basis_eur');

  const lines = [FINNISH_CAPITAL_GAINS_HEADERS.map(escapeCsvField).join(',')];

  for (let i = 0; i < details.length; i++) {
    const d = details[i];
    const rowNum = i + 2;
    const day = utcDateKeyFromTs(d.ts);
    const fx = rateByDay.get(day);
    const rateStr = fx != null ? fx.rate.toFixed(8) : '';
    const rateDateStr = fx?.rateDateIso ?? '';
    const cro = croByDay.get(day);
    const croUsdStr = cro?.usd ?? '';
    const croEurStr = cro?.eur ?? '';

    const sale = formatUtcDateTimeFi(d.ts);
    const acq = d.lotAcquiredAt != null ? formatUtcDateTimeFi(d.lotAcquiredAt) : '';
    const fifoCost = d.fifoCostBasisEur ?? d.costEur;
    const deemedCost = d.deemedCostBasisEur ?? d.proceedsEur.mul(0.2);
    const chosenCost = d.chosenCostBasisEur ?? fifoCost;
    const fifoGain = d.fifoGainEur ?? d.gainEur;
    const deemedGain = d.deemedGainEur ?? d.proceedsEur.minus(deemedCost);
    const chosenGain = d.chosenGainEur ?? d.gainEur;
    const method = d.basisMethodUsed ?? TAX_PIPELINE.fi.basisMethod;
    const bracket = d.deemedHoldingBracket ?? '';
    const deriv = d.proceedsDerivation ?? '';
    const vflag = d.valuationFlag ?? '';
    const deemedPrev = d.deemedPreventedDueToZeroProceeds === true ? 'yes' : '';
    const lr = d.lotBasisRepair;

    // Excel (Finnish locale): IF(ISNUMBER(manual); manual*rate; 0) — blank manual → 0 in helper columns.
    const proceedsFromManualFormula = `IF(ISNUMBER(${colManual}${rowNum})${XL}${colManual}${rowNum}*${colRate}${rowNum}${XL}0)`;
    const gainIfManualFormula = `IF(ISNUMBER(${colManual}${rowNum})${XL}${colProceedsManual}${rowNum}-${colFifo}${rowNum}${XL}0)`;

    lines.push(
      [
        meta.policyMode,
        meta.basisMethod,
        meta.timezone,
        auditNote,
        formatAssetAmountDisplay(d.disposed),
        d.asset,
        sale,
        acq,
        d.proceedsEur.toFixed(6),
        deriv,
        vflag,
        fifoCost.toFixed(6),
        deemedCost.toFixed(6),
        chosenCost.toFixed(6),
        method,
        deemedPrev,
        fifoGain.toFixed(6),
        deemedGain.toFixed(6),
        chosenGain.toFixed(6),
        bracket,
        lr ? 'yes' : '',
        lr?.basisRepairMethod ?? '',
        lr?.basisRepairConfidence ?? '',
        lr?.basisRepairSourceRef ?? '',
        rateStr,
        rateDateStr,
        croUsdStr,
        croEurStr,
        '',
        `=${proceedsFromManualFormula}`,
        `=${gainIfManualFormula}`,
      ]
        .map(escapeCsvField)
        .join(',')
    );
  }
  return lines.join('\r\n');
}
